import { Box, Button, Snackbar, Tooltip, useTheme } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { FocusEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import RequiredIndicator from '@/components/common/RequiredIndicator';
import ConfirmationModal from '@/components/common/ConfirmationModal';
import { Spinner } from '@/components/common/Spinner';
import { CurrentApplicationContext } from '@/Contexts/CurrentApplicationContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import useApplicationCycle from '@/hooks/useApplicationCycle';
import useAutosave from '@/hooks/useAutosave';
import useQuestions from '@/hooks/useQuestions';
import { EventProps } from '@/models/Event';
import { EventTypeProps } from '@/models/EventType';
import { QuestionProps } from '@/models/Question';

import DataUploadWrap from '../question_types/data_upload/DataUploadWrap';
import SavedIndicator from '../SavedIndicator';
import EventDescription from './EventDescription';
import EventName from './EventName';
import EventSubType from './EventSubType';
import NumParticipants from './NumParticipants';
import { logError } from '@/utils/errorHandlerUtil';

// Renders a single event's form. When mounted with no eventId it saves itself to the
// server immediately (instead of waiting for the user to fill in a field). Once that
// create resolves, every subsequent field edit is an update against the real event id -
// there's no in-between "unsaved local form" state to lose.
export default function Event({
  question,
  eventId,
  eventType,
  onChange,
  onDelete,
}: {
  question: QuestionProps;
  eventId?: number;
  eventType: EventTypeProps | null;
  onChange: (event: EventProps) => Promise<void> | void;
  onDelete: (eventInfo: EventProps) => Promise<void>;
}) {
  const theme = useTheme();
  const { getMaxDate, getMinDate, validDateTime } = useQuestions();
  const { translatedText } = useContext(LocaleContext);

  const { getApplicationCycle } = useApplicationCycle();
  const { currentApplication, canEdit, allEvents } = useContext(CurrentApplicationContext);
  const [open, setOpen] = useState<boolean>(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState<boolean>(false);
  const [uploadMessage, setUploadMessage] = useState<string>('');
  const [saveFailed, setSaveFailed] = useState<boolean>(false);
  const [dateError, setDateError] = useState<string | null>(null);
  const [eventDateDraft, setEventDateDraft] = useState<Dayjs | null>(null);
  const debounceTimeMs = 1500;

  const currentEvent: EventProps | undefined = useMemo(() => {
    if (eventId == null || allEvents == null) return undefined;

    return allEvents.find((x: EventProps) => x.id == eventId);
  }, [allEvents, eventId]);

  const [eventInfo, setEventInfo] = useState<EventProps | null>(null);

  // set the event info if it is not set
  useEffect(() => {
    if (eventInfo == null && currentEvent != null) setEventInfo(currentEvent);
  }, [currentEvent, eventInfo]);

  // Once the create call below resolves, eventId flips from undefined to the real id.
  // Merge it into the local draft in place so edits made while the create was in
  // flight aren't lost, and further saves go through the "update" path.
  useEffect(() => {
    if (eventId == null) return;
    setEventInfo((prev) => (prev != null && prev.id == null ? { ...prev, id: eventId } : prev));
  }, [eventId]);

  const hasTriggeredCreateRef = useRef(false);

  useEffect(() => {
    if (eventId != null) return;
    if (hasTriggeredCreateRef.current) return;
    if (!canEdit) return;

    hasTriggeredCreateRef.current = true;

    const blankEvent: EventProps = {
      applicationId: currentApplication.id,
      description: null,
      eventDate: null,
      eventName: null,
      eventSubTypeId: null,
      eventSubTypeOther: null,
      eventTypeId: question?.eventType?.id,
      involvedOrganization: 1,
      numParticipants: null,
      otherInvolvedOrganization: '1',
      questionId: question.id,
    };

    setEventInfo(blankEvent);

    Promise.resolve(onChange(blankEvent)).catch((error) => {
      setSaveFailed(true);
      logError('Error creating event question.', error);
    });
  }, [eventId, canEdit, currentApplication.id, question?.eventType?.id, question.id, onChange]);

  useEffect(() => {
    if (eventInfo == null) return;
    setEventDateDraft(eventInfo.eventDate != null ? dayjs(eventInfo.eventDate) : null);
  }, [eventInfo]);

  const saveEvent = useCallback(
    (updatedEvent: EventProps | null) => {
      if (updatedEvent == null) return;

      setSaveFailed(false);
      return Promise.resolve(onChange(updatedEvent)).catch((error) => {
        setSaveFailed(true);
        logError('Error autosaving event question.', error);
      });
    },
    [onChange]
  );

  const { saved, isSaving, markTouched, triggerSave } = useAutosave(
    eventInfo,
    saveEvent,
    debounceTimeMs
  );

  const updateEvent = useCallback(
    (updatedEvent: EventProps) => {
      markTouched();
      setEventInfo(updatedEvent);
    },
    [markTouched]
  );

  const applicationCycle = useMemo(() => {
    if (currentApplication != null) {
      const cycle = getApplicationCycle(currentApplication.applicationCycleId);
      return cycle ?? null;
    }
    return null;
  }, [currentApplication, getApplicationCycle]);

  const minDate = useMemo(
    () => getMinDate(question, applicationCycle),
    [question, applicationCycle, getMinDate]
  );

  const maxDate = useMemo(
    () => getMaxDate(question, applicationCycle),
    [question, applicationCycle, getMaxDate]
  );

  const dateRangeErrorMessage = useMemo(() => {
    const template = translatedText.event.dateRangeError;

    return template
      .replace('{{minDate}}', minDate.format('MMM D, YYYY'))
      .replace('{{maxDate}}', maxDate.format('MMM D, YYYY'));
  }, [translatedText.event.dateRangeError, minDate, maxDate]);

  const handleClose = () => {
    setOpen(false);
  };

  function onlyAllowNumbers(v: string | null): number | null {
    const cVal = v;
    const rVal = Number(cVal?.replace(/\D/g, ''));
    const nVal = !isNaN(rVal) && cVal !== '' ? rVal : null;

    return nVal;
  }

  function selectOnFocus(e: FocusEvent<HTMLInputElement>) {
    e.target.select();
  }

  const handleEventDateChange = useCallback(
    (newValue: Dayjs | null) => {
      setEventDateDraft(newValue);

      if (newValue == null) {
        setDateError(null);
        updateEvent({
          ...eventInfo,
          eventDate: null,
        });
        return;
      }

      // Ignore partial/invalid typed values until the date becomes parseable.
      if (!dayjs(newValue).isValid()) return;

      const inRange =
        dayjs(newValue).isValid() &&
        (dayjs(newValue).isSame(minDate, 'day') || dayjs(newValue).isAfter(minDate)) &&
        (dayjs(newValue).isSame(maxDate, 'day') || dayjs(newValue).isBefore(maxDate));

      if (!inRange) {
        setDateError(dateRangeErrorMessage);
        return;
      }

      if (
        applicationCycle != null &&
        !validDateTime(question, applicationCycle, newValue) &&
        !dayjs(newValue).isSame(minDate, 'day') &&
        !dayjs(newValue).isSame(maxDate, 'day')
      ) {
        setDateError(dateRangeErrorMessage);
        return;
      }

      setDateError(null);

      let eventDate = null;
      try {
        eventDate = newValue != null ? newValue?.toISOString() : null;
      } catch (err) {
        logError(`Event Question - date conversion error. Raw value: ${newValue?.toString()}`, err);
      }

      updateEvent({
        ...eventInfo,
        eventDate: eventDate,
      });
    },
    [
      applicationCycle,
      dateRangeErrorMessage,
      eventInfo,
      maxDate,
      minDate,
      question,
      updateEvent,
      validDateTime,
    ]
  );

  const datePickerLabel = (
    <span>
      {question?.eventType?.displayName ? translatedText?.event?.labelName : question.questionText}
      {question.questionNeeded ? <RequiredIndicator /> : ''}
    </span>
  );

  const handleDelete = useCallback(async () => {
    if (eventInfo == null) return;

    await onDelete(eventInfo);
    setConfirmDeleteOpen(false);
    setUploadMessage(translatedText?.event?.messageDelete);
    setOpen(true);
  }, [eventInfo, onDelete, translatedText?.event?.messageDelete]);

  const openDeleteConfirmation = useCallback(() => {
    setConfirmDeleteOpen(true);
  }, []);

  const closeDeleteConfirmation = useCallback(() => {
    setConfirmDeleteOpen(false);
  }, []);

  return eventInfo == null || question?.eventType == null ? (
    <Spinner type='circular' />
  ) : (
    <Box display='flex' flexDirection='row'>
      <Box
        border={`1px solid ${theme.palette.sandShade3.main}`}
        borderRadius='10px'
        padding='20px'
        width='50dvw'
        position='relative'
        sx={{ backgroundColor: theme.palette.sandTint.main }}
      >
        {eventInfo?.id != null && (
          <Tooltip title={translatedText.event.tooltipDelete}>
            <Button
              variant='secondary'
              size='small'
              disabled={!canEdit}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
              }}
              onClick={openDeleteConfirmation}
            >
              {translatedText.common.buttonDelete}
            </Button>
          </Tooltip>
        )}

        <Box display='flex' flexDirection='column' gap='16px' sx={{ paddingTop: '16px' }}>
          {question?.eventType?.displayName && (
            <EventName
              translatedText={translatedText}
              question={question}
              eventInfo={eventInfo}
              onEventChange={updateEvent}
              questionsEditable={canEdit}
              selectOnFocus={selectOnFocus}
            />
          )}

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={datePickerLabel}
              views={['year', 'month', 'day']}
              value={eventDateDraft}
              disabled={!canEdit}
              onChange={handleEventDateChange}
              // minDate={minDate}
              // maxDate={maxDate} dont use these - they're causing confusion for users
              slotProps={{
                textField: {
                  error: dateError != null,
                  helperText: dateError,
                },
              }}
            />
          </LocalizationProvider>
          {question?.eventType?.displayEventSubType && (
            <EventSubType
              question={question}
              eventInfo={eventInfo}
              subTypes={eventType?.eventSubTypes ?? null}
              onEventChange={updateEvent}
            />
          )}
          {question?.eventType?.displayNumParticipants && (
            <NumParticipants
              question={question}
              eventInfo={eventInfo}
              onEventChange={updateEvent}
              selectOnFocus={selectOnFocus}
              onlyAllowNumbers={onlyAllowNumbers}
            />
          )}
          {question?.eventType?.displayDescription && (
            <EventDescription
              question={question}
              eventInfo={eventInfo}
              onEventChange={updateEvent}
              selectOnFocus={selectOnFocus}
            />
          )}
          {question?.eventType?.displayProof && (
            <DataUploadWrap question={question} eventInfo={eventInfo} />
          )}
        </Box>

        <ConfirmationModal
          open={confirmDeleteOpen}
          onClose={closeDeleteConfirmation}
          header={translatedText?.event?.confirmationModal?.header}
          confirmationText={translatedText?.event?.confirmationModal?.confirmationMessage}
          confirmButtonText={translatedText?.event?.confirmationModal?.confirmationButtonText}
          onDelete={handleDelete}
        />
        <Snackbar
          open={open}
          autoHideDuration={3000}
          onClose={handleClose}
          message={uploadMessage}
        />
      </Box>
      <SavedIndicator
        saved={saved}
        saveFailed={saveFailed}
        isSaving={isSaving.current}
        saveFn={triggerSave}
        sx={{ alignSelf: 'center' }}
      />
    </Box>
  );
}
